import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingCart, Check, Download, Shield, RefreshCw, CheckCircle2 } from "lucide-react";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import { useAuth } from "../contexts/AuthContext";
import { isCartomancerProduct, cartomancerLogoFor } from "../branding";
import SeoHead from "../components/SeoHead";
import ImageLightbox from "../components/ImageLightbox";
import PayPalCheckoutButton from "../components/PayPalCheckoutButton";

const ProductDetailPage = () => {
  const { t } = useTranslation();
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [owned, setOwned] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (user?.user_id) checkOwnership();
    else setOwned(false);
  }, [user?.user_id, productId]);

  const fetchProduct = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`,
      );
      const data = await response.json();
      setProduct(data);
    } catch (error) {
      console.error("Failed to fetch product:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkOwnership = async () => {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/me/licenses`,
        { credentials: "include" },
      );
      if (!resp.ok) return;
      const data = await resp.json();
      const activeIds = new Set(
        (data.licenses || [])
          .filter((l) => l.status === "active")
          .map((l) => l.product_id),
      );
      const hasPremium = activeIds.has("cartomancer_premium");
      const has =
        activeIds.has(productId) ||
        (productId === "cartomancer_basic" && hasPremium);
      setOwned(has);
    } catch {
      // silent
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    setPurchasing(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/create-checkout-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            lookup_key: product.lookup_key,
            success_url: `${window.location.origin}/payment/success`,
            cancel_url: window.location.href,
            user_id: user?.user_id,
          }),
        },
      );

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error("No checkout URL returned");
        setPurchasing(false);
      }
    } catch (error) {
      console.error("Purchase failed:", error);
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-16 h-16 border-4 border-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display text-3xl text-foreground mb-4">
            {t("product.notFound")}
          </h2>
          <Button onClick={() => navigate("/store")}>{t("product.backToStore")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-6">
      <SeoHead
        title={product?.name ? `${product.name} — Store` : t("product.loading")}
        description={
          product?.description
            ? String(product.description).slice(0, 180)
            : t("product.defaultDesc")
        }
        path={`/product/${product?.product_id || ""}`}
        type="product"
        image={product?.images?.[0] || undefined}
        jsonLd={
          product
            ? {
                "@context": "https://schema.org",
                "@type": "Product",
                name: product.name,
                description: product.description || undefined,
                image: product.images || undefined,
                brand: { "@type": "Brand", name: "Mytharion" },
                offers: {
                  "@type": "Offer",
                  price: String(product.final_price || product.base_price || 0),
                  priceCurrency: "EUR",
                  availability: "https://schema.org/InStock",
                  url: `https://mytharion.app/product/${product.product_id}`,
                },
              }
            : null
        }
      />
      <div className="container mx-auto max-w-6xl">
        <button
          onClick={() => navigate("/store")}
          className="text-muted-foreground hover:text-gold transition-colors mb-8 font-heading text-sm"
          data-testid="product-back"
        >
          {t("product.backLink")}
        </button>

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div className="space-y-4">
            {(() => {
              const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
              const resolveUrl = (u) => (!u ? u : u.startsWith("http") ? u : `${BACKEND}${u}`);
              const rawGallery = Array.isArray(product?.images)
                ? product.images.filter(Boolean)
                : [];
              const gallery = rawGallery.map(resolveUrl);
              const hasGallery = gallery.length > 0;
              const isCartomancerFallback =
                !hasGallery && isCartomancerProduct(product?.product_id);
              return (
                <>
                  <div
                    className={`rune-border rounded-xl overflow-hidden bg-gradient-to-br from-arcane/10 via-card to-gold/5 flex items-center justify-center h-96 ${
                      hasGallery ? "cursor-zoom-in group" : ""
                    }`}
                    onClick={hasGallery ? () => setLightboxIndex(0) : undefined}
                    role={hasGallery ? "button" : undefined}
                    tabIndex={hasGallery ? 0 : undefined}
                    onKeyDown={
                      hasGallery
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setLightboxIndex(0);
                            }
                          }
                        : undefined
                    }
                    aria-label={hasGallery ? t("product.openGallery") : undefined}
                    data-testid="product-image-hero"
                  >
                    {hasGallery ? (
                      <img
                        src={gallery[0]}
                        alt={product?.name || "Product"}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : isCartomancerFallback ? (
                      <img
                        src={cartomancerLogoFor(product?.product_id)}
                        alt={
                          product?.product_id === "cartomancer_premium"
                            ? "Cartomancer Premium"
                            : "Cartomancer"
                        }
                        className="max-h-[85%] max-w-[85%] object-contain drop-shadow-[0_10px_28px_rgba(217,178,52,0.45)]"
                        data-testid="product-detail-cartomancer-logo"
                      />
                    ) : (
                      <img
                        src="https://via.placeholder.com/800x600/1a1a2e/d4af37?text=Loading"
                        alt={product?.name || "Product"}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>

                  {gallery.length > 1 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {gallery.slice(1).map((img, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setLightboxIndex(idx + 1)}
                          className="border border-border hover:border-gold rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-gold transition-colors group"
                          data-testid={`thumbnail-${idx + 1}`}
                          aria-label={t("product.openImage", { n: idx + 2 })}
                        >
                          <img
                            src={img}
                            alt={`${product.name} ${idx + 2}`}
                            className="w-full h-24 object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div>
            <div className="flex items-center gap-3 mb-4">
              <h1 className="font-display text-5xl text-gold">
                {product?.name || t("product.loading")}
              </h1>
            </div>

            <div className="flex items-center gap-3 mb-4">
              {product?.subscription ? (
                <Badge variant="arcane">{t("product.badgeSubscription")}</Badge>
              ) : (
                <Badge variant="gold">{t("product.badgeOneTime")}</Badge>
              )}
            </div>

            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              {product?.description || t("product.loading")}
            </p>

            <div className="rune-border bg-card/50 backdrop-blur p-6 rounded-xl mb-8">
              <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                {product?.discount_active &&
                  Number(product?.final_price) < Number(product?.base_price) && (
                    <span
                      className="font-heading text-2xl text-ruby line-through"
                      data-testid="detail-price-base"
                    >
                      €{Number(product?.base_price || 0).toFixed(2)}
                    </span>
                  )}
                <span
                  className="font-display text-5xl text-gold"
                  data-testid="detail-price-final"
                >
                  €{Number(product?.final_price || 0).toFixed(2)}
                </span>
                {product?.subscription && (
                  <span className="font-heading text-lg text-muted-foreground">
                    {t("product.monthly")}
                  </span>
                )}
                <span
                  className="font-heading text-xs text-muted-foreground uppercase tracking-wider"
                  data-testid="detail-price-tax-hint"
                >
                  {t("product.vatExcluded")}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {product?.subscription
                  ? t("product.billingMonthly")
                  : t("product.oneTimeDesc")}
              </p>

              {owned ? (
                <>
                  <Button
                    variant="secondary"
                    size="lg"
                    className="w-full"
                    disabled
                    data-testid="detail-owned-btn"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    {t("product.alreadyOwned")}
                  </Button>
                  {isCartomancerProduct(product?.product_id) && (
                    <button
                      onClick={() => navigate("/app")}
                      className="mt-2 w-full text-sm text-gold hover:text-gold/80 font-heading transition-colors"
                      data-testid="detail-open-app"
                    >
                      {t("product.openWebapp")}
                    </button>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <Button
                    onClick={handlePurchase}
                    disabled={purchasing}
                    variant={product?.subscription ? "arcane" : "primary"}
                    size="lg"
                    className="w-full"
                    data-testid="detail-buy-btn"
                  >
                    {purchasing ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin w-5 h-5 border-2 border-current border-t-transparent rounded-full mr-2"></div>
                        {t("product.redirecting")}
                      </div>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        {product?.subscription
                          ? t("product.payStripeSub")
                          : t("product.payStripe")}
                      </>
                    )}
                  </Button>

                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 h-px bg-border"></div>
                    <span className="text-xs uppercase tracking-widest text-muted-foreground font-heading">
                      {t("product.or")}
                    </span>
                    <div className="flex-1 h-px bg-border"></div>
                  </div>

                  {user ? (
                    <PayPalCheckoutButton
                      product={product}
                      userId={user?.user_id}
                      onSuccess={() => navigate("/payment/success?provider=paypal")}
                      onError={(err) => console.error("PayPal:", err)}
                    />
                  ) : (
                    <Button
                      variant="secondary"
                      size="lg"
                      className="w-full"
                      onClick={() => navigate("/login")}
                      data-testid="paypal-login-required"
                    >
                      {t("product.loginPaypal")}
                    </Button>
                  )}
                </div>
              )}

              {!user && !owned && (
                <p className="text-xs text-center text-muted-foreground mt-3">
                  {t("product.loginPrompt")}
                </p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <Shield className="w-6 h-6 text-gold mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">{t("product.trustSecure")}</p>
              </div>
              <div>
                <RefreshCw className="w-6 h-6 text-gold mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">{t("product.trustTrial")}</p>
              </div>
              <div>
                <Download className="w-6 h-6 text-gold mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">{t("product.trustDownload")}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="font-display text-3xl text-foreground mb-8">
            {t("product.featuresTitle")}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {product?.features?.map((feature, idx) => (
              <div key={idx} className="flex items-start p-4 rune-border bg-card/30 rounded-lg">
                <Check className="w-5 h-5 text-gold mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rune-border bg-card/50 backdrop-blur p-8 rounded-xl">
          <h3 className="font-heading text-xl text-gold mb-4">
            {t("product.requirementsTitle")}
          </h3>
          <ul className="space-y-2">
            {product?.requirements?.map((req, idx) => (
              <li key={idx} className="text-muted-foreground text-sm flex items-center">
                <span className="w-2 h-2 bg-gold rounded-full mr-3"></span>
                {req}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {lightboxIndex != null &&
        Array.isArray(product?.images) &&
        product.images.length > 0 && (
          <ImageLightbox
            images={product.images.map((u) =>
              !u
                ? u
                : u.startsWith("http")
                ? u
                : `${process.env.REACT_APP_BACKEND_URL || ""}${u}`,
            )}
            startIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />
        )}
    </div>
  );
};

export default ProductDetailPage;
